import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  GuestMe,
  PlayerFallback,
  PublicState,
  ReactionEvent,
  ServerToClientEvents,
  SocketAuth,
  Toast,
} from '@karozik/shared';

export type PartySocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export interface FloatingReaction extends ReactionEvent {
  key: number;
  drift: number;
  left: number;
}

export interface PartyConnection {
  socket: PartySocket | null;
  state: PublicState | null;
  me: GuestMe | null;
  connected: boolean;
  error: string | null;
  toasts: (Toast & { key: number })[];
  reactions: FloatingReaction[];
  fallback: PlayerFallback | null;
  /** Offset to add to Date.now() to get the server clock. */
  clockSkew: number;
  dismissToast(key: number): void;
  /** Emits an acknowledged event; resolves with the server's verdict. */
  send<E extends AckEvent>(event: E, payload: Parameters<ClientToServerEvents[E]>[0]): Promise<{ ok: boolean; error?: string }>;
}

type AckEvent = 'propose' | 'vote' | 'skipVote' | 'blindtest:answer' | 'faces:answer' | 'control';

let keySeq = 0;

/**
 * One Socket.IO connection to a party, with the latest state, toasts and the
 * stream of floating reactions. Re-connects by itself; `auth` changes (kiosk
 * session start/end) open a fresh connection.
 */
export function useParty(auth: SocketAuth | null, opts: { keepReactions?: boolean } = {}): PartyConnection {
  const [socket, setSocket] = useState<PartySocket | null>(null);
  const [state, setState] = useState<PublicState | null>(null);
  const [me, setMe] = useState<GuestMe | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<(Toast & { key: number })[]>([]);
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [fallback, setFallback] = useState<PlayerFallback | null>(null);
  const [clockSkew, setClockSkew] = useState(0);
  const authKey = auth ? JSON.stringify(auth) : null;
  const keepReactions = opts.keepReactions ?? true;
  const keepRef = useRef(keepReactions);
  keepRef.current = keepReactions;

  useEffect(() => {
    if (!authKey) return;
    const s: PartySocket = io({ auth: JSON.parse(authKey), transports: ['websocket', 'polling'] });
    setSocket(s);
    s.on('connect', () => {
      setConnected(true);
      setError(null);
    });
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', (e) => setError(e.message));
    s.on('state', (st) => {
      setState(st);
      setClockSkew(st.serverTime - Date.now());
    });
    s.on('me', setMe);
    s.on('player:fallback', setFallback);
    s.on('toast', (t) => {
      const key = ++keySeq;
      setToasts((list) => [...list.slice(-3), { ...t, key }]);
      setTimeout(() => setToasts((list) => list.filter((x) => x.key !== key)), 5000);
    });
    s.on('reaction', (r) => {
      if (!keepRef.current) return;
      const key = ++keySeq;
      setReactions((list) => [...list.slice(-40), { ...r, key, drift: Math.round((Math.random() - 0.5) * 160), left: 5 + Math.random() * 90 }]);
      setTimeout(() => setReactions((list) => list.filter((x) => x.key !== key)), 3400);
    });
    return () => {
      s.removeAllListeners();
      s.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [authKey]);

  const send = useCallback<PartyConnection['send']>(
    (event, payload) =>
      new Promise((resolve) => {
        if (!socket?.connected) return resolve({ ok: false, error: 'Connexion perdue, on réessaie…' });
        const timer = setTimeout(() => resolve({ ok: false, error: 'Le serveur ne répond pas.' }), 8000);
        (socket.emit as (e: string, p: unknown, ack: (r: { ok: boolean; error?: string }) => void) => void)(event, payload, (r) => {
          clearTimeout(timer);
          resolve(r);
        });
      }),
    [socket],
  );

  const dismissToast = useCallback((key: number) => setToasts((list) => list.filter((t) => t.key !== key)), []);

  return { socket, state, me, connected, error, toasts, reactions, fallback, clockSkew, dismissToast, send };
}

/** Re-renders every `ms` — for countdowns and progress bars. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/** Debounced value. */
export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
